import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OnboardingModule } from '@app/features/onboarding/onboarding.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AttachmentTypeService } from '@services/http/attachment-type.service';
import { CompanyService } from '@services/http/company.service';
import { DashboardService } from '@services/http/dashboard.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { TenantService } from '@services/http/tenant.service';
import { UserApiTokenService } from '@services/http/user-api-token.service';
import { UserService } from '@services/http/user.service';
import { SharedModule } from '@shared/shared.module';
import { NdfEditDemandComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-edit-demand/ndf-edit-demand.component';
import { AnalysisDocumentPillComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-document-pill/analysis-document-pill.component';
import { AnalysisDocumentRowComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-document-row/analysis-document-row.component';
import { AnalysisFooterBarComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-footer-bar/analysis-footer-bar.component';
import { AnalysisPageSkeletonComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-page-skeleton/analysis-page-skeleton.component';
import { AnalysisStageSectionComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-stage-section/analysis-stage-section.component';
import { AnalysisSummaryCardsComponent } from '@src/app/features/dashboard/document-analysis-company/components/analysis-summary-cards/analysis-summary-cards.component';
import { CompanyAnalysisHeaderComponent } from '@src/app/features/dashboard/document-analysis-company/components/company-analysis-header/company-analysis-header.component';
import { CompanyRegistrationCardComponent } from '@src/app/features/dashboard/document-analysis-company/components/company-registration-card/company-registration-card.component';
import { DocumentTemplateComponent } from '@src/app/shared/components/document-template/document-template.component';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { PopoverModule } from 'primeng/popover';

import { ConsolidatedDueDateHttpService } from '@services/http/consolidated-due-date/consolidated-due-date-http.service';
import { FloatingAiButtonComponent } from '@shared/components/floating-ai-button/floating-ai-button.component';
import { UpdateNotificationBannerComponent } from '@shared/components/update-notification-banner/update-notification-banner.component';
import { BusinessCaseModule } from '@src/app/features/dashboard/business-case/business-case.module';
import { ModalBankContactsComponent } from '@src/app/features/dashboard/market/banking-relationship/components/modal-bank-contacts/modal-bank-contacts.component';
import { CompanyContactsModalComponent } from '@src/app/features/dashboard/company/company-contacts/company-contacts-modal.component';
import { CompanyContactsPanelComponent } from '@src/app/features/dashboard/company/company-contacts/company-contacts-panel.component';
import { OnboardingSelectCompanyModalComponent } from '@src/app/features/onboarding/components/onboarding-select-company-modal/onboarding-select-company-modal.component';
import { OnboardingV2ViewModalComponent } from '@src/app/features/onboarding/v2/modals/onboarding-v2-view-modal/onboarding-v2-view-modal.component';
import ModalComexOperationDetailsComponent from '@src/app/features/dashboard/comex/pages/modal-comex-operation-details/modal-comex-operation-details.component';
import { ModalBusinessCaseUploadComponent } from '@src/app/features/dashboard/company/company-list/components/company-business-case-upload-modal.component';
import { CompanyBenchmarkModalComponent } from '@src/app/features/dashboard/company/company-list/components/modal/company-invoicing-modal/company-benchmark-modal/company-benchmark-modal.component';
import { CompanyInvoicingModalComponent } from '@src/app/features/dashboard/company/company-list/components/modal/company-invoicing-modal/company-invoicing-modal.component';
import { CompanyInvoicingParametersModalComponent } from '@src/app/features/dashboard/company/company-list/components/modal/company-invoicing-modal/company-invoicing-parameters-modal/company-invoicing-parameters-modal.component';
import { TransferCompanyModalComponent } from '@src/app/features/dashboard/company/company-list/components/modal/transfer-company-modal/transfer-company-modal.component';
import { CreateOperatinsWithCogModalComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-management/create-operations-with-cog/create-operations-with-cog-modal.component';
import { ModalMtmDailyComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives/components/modal-mtm-daily/modal-mtm-daily.component';
import DerivativeMtmResultsModalComponent from '@src/app/features/dashboard/control/derivative-management/mtm/modal/derivative-mtm-results.modal';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { ModalDerivativeAttachmentsComponent } from '@src/app/shared/components/modal/modal-derivative-attachments/modal-derivative-attachments.component';
import { NotificationPreferencesFormComponent } from '@src/app/shared/components/notification-preferences-form/notification-preferences-form.component';
import { UserEmailSignatureFormComponent } from './user-register/components/user-email-signature-form/user-email-signature-form.component';
import { AiModule } from './ai/ai.module';
import { AttachmentTypeListContentComponent } from './attachment-type-list/attachment-type-list-content/attachment-type-list-content.component';
import { AttachmentTypeListComponent } from './attachment-type-list/attachment-type-list.component';
import { AttachmentTypeRegisterComponent } from './attachment-type-register/attachment-type-register.component';
import { CompanyListContentComponent } from './company/company-list/company-list-content/company-list-content.component';
import { CompanyListComponent } from './company/company-list/company-list.component';
import { CompanyRegisterComponent } from './company/company-register/company-register.component';
import { CompanyEmailSlugFieldComponent } from './company/company-register/company-email-slug-field/company-email-slug-field.component';
import { CompanySectorFieldComponent } from './company/company-register/company-sector-field/company-sector-field.component';
import { CompanySectorBulkActionsComponent } from './company/company-list/components/company-sector-bulk-actions/company-sector-bulk-actions.component';
import { CompanySectorFilterComponent } from './company/company-list/components/company-sector-filter/company-sector-filter.component';
import { CompanyAutoRegistrationBannerComponent } from './company/company-register/company-auto-registration-banner/company-auto-registration-banner.component';
import { ClientContractsComponent } from './company/contracts/components/client-contracts/client-contracts.component';
import { NdfCalculatorFilterComponent } from './control/derivative-management/derivatives-old-version/ndf-calculator-list/ndf-calculator-filter/ndf-calculator-filter.component';
import { NdfCalculatorListComponent } from './control/derivative-management/derivatives-old-version/ndf-calculator-list/ndf-calculator-list.component';
import { NdfCardSummaryComponent } from './control/derivative-management/derivatives-old-version/ndf-calculator-list/ndf-card-summary/ndf-card-summary.component';
import { NdfManagementGridComponent } from './control/derivative-management/derivatives-old-version/ndf-management/ndf-management-grid/ndf-management-grid.component';
import { NdfManagementItemComponent } from './control/derivative-management/derivatives-old-version/ndf-management/ndf-management-item/ndf-management-item.component';
import { NdfManagementComponent } from './control/derivative-management/derivatives-old-version/ndf-management/ndf-management.component';
import { NdfOperationCardSummaryComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/ndf-operation-card-summary/ndf-operation-card-summary.component';
import { NdfOperationFilterComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/ndf-operation-filter/ndf-operation-filter.component';
import { NdfOperationListComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/ndf-operation-list.component';
import { ModalConfirmLiquidationComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/modal-confirm-liquidation/modal-confirm-liquidation.component';
import { ModalLiquidateOperationComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/modal-liquidate-operation/modal-liquidate-operation.component';
import { ModalSaveReversalConfirmationComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/modal-save-reversal-confirmation/modal-save-reversal-confirmation.component';
import { ModalCreateCommitteeComponent } from './control/risk-management/monitoring/pages/committee/components/modal-create-committee/modal-create-committee.component';
import { DerivativeAssetsModule } from './control/derivative-management/reports/derivative-assets/derivative-assets.module';
import { HomeCogSearchComponent } from './dashboard-management/components/home-cog-search/home-cog-search.component';
import { HomeBankLimitsCardComponent } from './dashboard-management/components/home-bank-limits-card/home-bank-limits-card.component';
import { HomeMeetingsCardComponent } from './dashboard-management/components/home-meetings-card/home-meetings-card.component';
import { HomeCustomizeDrawerComponent } from './dashboard-management/components/home-customize-drawer/home-customize-drawer.component';
import { CreditScoreStripComponent } from './performance/pages/dfp/components/credit-score-strip/credit-score-strip.component';
import { EconomyScenarioCardsComponent } from './dashboard-management/components/economy-scenario-cards/economy-scenario-cards.component';
import { DebtPerformanceChartComponent } from './dashboard-management/components/debt-performance-chart/debt-performance-chart.component';
import { DashboardManagementComponent } from './dashboard-management/dashboard-management.component';
import { DashboardClassicComponent } from './dashboard-management/dashboard-classic.component';
import { Di1CurveChartComponent } from './dashboard-management/components/di1-curve-chart/di1-curve-chart.component';
import { KeyIndicatorsSectionComponent } from './dashboard-management/components/key-indicators-section/key-indicators-section.component';
import { PtaxCurveChartComponent } from './dashboard-management/components/ptax-curve-chart/ptax-curve-chart.component';
import { DashboardRoutingModule } from './dashboard-routing.module';
import { DashboardComponent } from './dashboard.component';
import { DocumentAnalysisCompanyComponent } from './document-analysis-company/document-analysis-company.component';
import { DocumentAnalysisCardsComponent } from './document-analysis/document-analysis-cards/document-analysis-cards.component';
import { DocumentAnalysisCompanyCardComponent } from './document-analysis/document-analysis-cards/document-analysis-company-card/document-analysis-company-card.component';
import { DocumentAnalysisCompositionBarComponent } from './document-analysis/document-analysis-composition-bar/document-analysis-composition-bar.component';
import { DocumentAnalysisSummaryComponent } from './document-analysis/document-analysis-summary/document-analysis-summary.component';
import { DocumentAnalysisTableComponent } from './document-analysis/document-analysis-table/document-analysis-table.component';
import { DocumentAnalysisToolbarComponent } from './document-analysis/document-analysis-toolbar/document-analysis-toolbar.component';
import { DocumentAnalysisComponent } from './document-analysis/document-analysis.component';
import { ContactNotesFieldsComponent } from '@shared/components/contact-notes-fields/contact-notes-fields.component';
import { ModalBankManagersComponent } from './financial-institution-list/components/modal-bank-managers/modal-bank-managers.component';
import { FinancialInstitutionListContentComponent } from './financial-institution-list/financial-institution-list-content/financial-institution-list-content.component';
import { FinancialInstitutionListComponent } from './financial-institution-list/financial-institution-list.component';
import { LoanOperationOriginsComponent } from './financial-institution-list/pages/loan-operation-origins/loan-operation-origins.component';
import { FinancialInstitutionRegisterComponent } from './financial-institution-register/financial-institution-register.component';
import { BillingParametersDialogComponent } from './management/back-office/pages/billing/components/dialogs/billing-parameters-dialog/billing-parameters-dialog.component';
import { MarketModule } from './market/market.module';
import { TenantListContentComponent } from './tenant-list/tenant-list-content/tenant-list-content.component';
import { TenantListComponent } from './tenant-list/tenant-list.component';
import { TenantRegisterComponent } from './tenant-register/tenant-register.component';
import { ModalApiTokensComponent } from './user-list/components/modal-api-tokens/modal-api-tokens.component';
import { UserFilterComponent } from './user-list/components/user-filter/user-filter.component';
import { UserSummaryCardsComponent } from './user-list/components/user-summary-cards/user-summary-cards.component';
import { UserListContentComponent } from './user-list/user-list-content/user-list-content.component';
import { UserListComponent } from './user-list/user-list.component';

import { UserRegisterComponent } from './user-register/user-register.component';
import { UserAccessPanelComponent } from './economic-groups/components/user-access-panel/user-access-panel.component';
import { CompanyPartnerModalComponent } from './company/company-register/company-partner-modal/company-partner-modal.component';

@NgModule({
  declarations: [
    DashboardComponent,
    UserListComponent,
    UserListContentComponent,
    UserRegisterComponent,
    FinancialInstitutionListComponent,
    ModalBankManagersComponent,
    AttachmentTypeListComponent,
    AttachmentTypeListContentComponent,
    AttachmentTypeRegisterComponent,
    TenantListComponent,
    TenantListContentComponent,
    TenantRegisterComponent,
    CompanyListComponent,
    CompanyListContentComponent,
    CompanyRegisterComponent,
    NdfCalculatorListComponent,
    NdfOperationListComponent,
    NdfCalculatorFilterComponent,
    NdfCardSummaryComponent,
    NdfManagementComponent,
    NdfManagementGridComponent,
    NdfManagementItemComponent,
    NdfOperationFilterComponent,
    NdfOperationCardSummaryComponent,
    DocumentAnalysisComponent,
    DocumentAnalysisCompositionBarComponent,
    DocumentAnalysisSummaryComponent,
    DocumentAnalysisToolbarComponent,
    DocumentAnalysisTableComponent,
    DocumentAnalysisCardsComponent,
    DocumentAnalysisCompanyCardComponent,
    NdfEditDemandComponent,
    CreateOperatinsWithCogModalComponent,
    DashboardManagementComponent,
    DashboardClassicComponent,
    DocumentAnalysisCompanyComponent,
  ],
  exports: [NdfEditDemandComponent, ModalApiTokensComponent],
  imports: [
    TableSkeletonComponent,
    CompanyEmailSlugFieldComponent,
    CompanySectorFieldComponent,
    CompanySectorBulkActionsComponent,
    CompanySectorFilterComponent,
    CompanyAutoRegistrationBannerComponent,
    ClientContractsComponent,
    CommonModule,
    ContactNotesFieldsComponent,
    ModalApiTokensComponent,
    UserFilterComponent,
    UserSummaryCardsComponent,
    ReactiveFormsModule,
    FormsModule,
    SharedModule,
    FontAwesomeModule,
    TableModule,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    DragDropModule,
    NgxMaskDirective,
    NgxMaskPipe,
    UserAccessPanelComponent,
    DashboardRoutingModule,
    ProgressSpinnerModule,
    ToastModule,
    ButtonModule,
    CardModule,
    DatePickerModule,
    InputNumberModule,
    MessageModule,
    PaginatorModule,
    AccordionModule,
    InputTextModule,
    DerivativeAssetsModule,
    MarketModule,
    AiModule,
    OnboardingModule,
    DialogModule,
    TooltipModule,
    PopoverModule,
    DocumentTemplateComponent,
    CompanyAnalysisHeaderComponent,
    AnalysisSummaryCardsComponent,
    CompanyRegistrationCardComponent,
    AnalysisDocumentPillComponent,
    AnalysisDocumentRowComponent,
    AnalysisStageSectionComponent,
    AnalysisFooterBarComponent,
    AnalysisPageSkeletonComponent,
    ModalBusinessCaseUploadComponent,
    CompanyInvoicingParametersModalComponent,
    CompanyBenchmarkModalComponent,
    CompanyInvoicingModalComponent,
    TransferCompanyModalComponent,
    BusinessCaseModule,
    DrawerModule,
    MultiSelectModule,
    FinancialInstitutionRegisterComponent,
    FinancialInstitutionListContentComponent,
    LoanOperationOriginsComponent,
    EconomyScenarioCardsComponent,
    DebtPerformanceChartComponent,
    HomeCogSearchComponent,
    HomeBankLimitsCardComponent,
    HomeMeetingsCardComponent,
    HomeCustomizeDrawerComponent,
    // Dashboard clássica (sem a flag dashboard-home)
    Di1CurveChartComponent,
    KeyIndicatorsSectionComponent,
    PtaxCurveChartComponent,
    CreditScoreStripComponent,
    ModalDerivativeAttachmentsComponent,
    ModalLoanDetailsComponent,
    ModalComexOperationDetailsComponent,
    DerivativeMtmResultsModalComponent,
    ModalMtmDailyComponent,
    ModalLiquidateOperationComponent,
    ModalConfirmLiquidationComponent,
    ModalSaveReversalConfirmationComponent,
    ModalCreateCommitteeComponent,
    UpdateNotificationBannerComponent,
    FloatingAiButtonComponent,
    BillingParametersDialogComponent,
    NotificationPreferencesFormComponent,
    UserEmailSignatureFormComponent,
    ModalBankContactsComponent,
    CompanyContactsModalComponent,
    CompanyContactsPanelComponent,
    OnboardingSelectCompanyModalComponent,
    OnboardingV2ViewModalComponent,
    CompanyPartnerModalComponent,
    MenuModule,
  ],
  providers: [
    UserService,
    UserApiTokenService,
    DatePipe,
    FinancialInstitutionService,
    AttachmentTypeService,
    TenantService,
    CompanyService,
    DashboardService,
    NdfCalculatorService,
    ConsolidatedDueDateHttpService,
  ],
})
export class DashboardModule {}
